export interface PosterDay {
  dateKey: string
  label: string
  value: number
}

export interface PosterPayload {
  siteName: string
  fromLabel: string
  toLabel: string
  days: PosterDay[]
  total: number
  peakLabel: string
  peakValue: number
  growthPct: number | null
  avg: number
}

const W = 1080
const H = 1350

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + w, y, x + w, y + h, radius)
  ctx.arcTo(x + w, y + h, x, y + h, radius)
  ctx.arcTo(x, y + h, x, y, radius)
  ctx.arcTo(x, y, x + w, y, radius)
  ctx.closePath()
}

function formatNumber(n: number) {
  return Math.max(0, Math.round(n)).toLocaleString('pt-PT')
}

export function createAnalyticsPosterCanvas() {
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  return canvas
}

export function drawAnalyticsPoster(canvas: HTMLCanvasElement, data: PosterPayload) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  canvas.width = W
  canvas.height = H

  ctx.fillStyle = '#0b0b0d'
  ctx.fillRect(0, 0, W, H)

  const bg = ctx.createLinearGradient(0, 0, W, H)
  bg.addColorStop(0, '#141218')
  bg.addColorStop(0.45, '#0c0c0f')
  bg.addColorStop(1, '#161016')
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, W, H)

  ctx.fillStyle = '#dc2626'
  ctx.fillRect(0, 0, W, 10)

  ctx.fillStyle = 'rgba(220, 38, 38, 0.16)'
  ctx.beginPath()
  ctx.arc(980, 90, 220, 0, Math.PI * 2)
  ctx.fill()

  ctx.fillStyle = 'rgba(255, 255, 255, 0.03)'
  ctx.beginPath()
  ctx.arc(80, 1220, 260, 0, Math.PI * 2)
  ctx.fill()

  ctx.fillStyle = '#f4f1ea'
  ctx.font = '700 22px Georgia, "Times New Roman", serif'
  ctx.fillText('CARLOS FELIPE', 72, 78)

  roundRect(ctx, 72, 98, 268, 40, 20)
  ctx.fillStyle = '#1f1b16'
  ctx.fill()
  ctx.strokeStyle = 'rgba(249, 171, 0, 0.55)'
  ctx.lineWidth = 1.5
  ctx.stroke()

  const dots = ['#EA4335', '#FBBC05', '#34A853', '#4285F4']
  dots.forEach((color, i) => {
    ctx.beginPath()
    ctx.fillStyle = color
    ctx.arc(94 + i * 16, 118, 5, 0, Math.PI * 2)
    ctx.fill()
  })
  ctx.fillStyle = '#f4f1ea'
  ctx.font = '600 16px system-ui, sans-serif'
  ctx.fillText('Google Analytics', 162, 123)

  ctx.fillStyle = '#f8f4ec'
  ctx.font = '700 58px Georgia, "Times New Roman", serif'
  ctx.fillText('Relatório de', 72, 210)
  ctx.fillText('visualizações', 72, 276)

  ctx.fillStyle = 'rgba(244, 241, 234, 0.62)'
  ctx.font = '500 24px system-ui, sans-serif'
  ctx.fillText(`${data.fromLabel}  —  ${data.toLabel}`, 72, 328)

  roundRect(ctx, 72, 368, 936, 196, 28)
  ctx.fillStyle = '#17151b'
  ctx.fill()
  ctx.strokeStyle = 'rgba(255,255,255,0.06)'
  ctx.lineWidth = 1
  ctx.stroke()

  const cards = [
    { label: 'TOTAL', value: formatNumber(data.total), x: 72 },
    { label: 'MÉDIA / DIA', value: formatNumber(data.avg), x: 384 },
    {
      label: 'vs. PERÍODO ANTERIOR',
      value:
        data.growthPct === null
          ? '—'
          : `${data.growthPct >= 0 ? '+' : ''}${data.growthPct}%`,
      x: 696,
    },
  ]

  cards.forEach((card, i) => {
    if (i > 0) {
      ctx.fillStyle = 'rgba(255,255,255,0.08)'
      ctx.fillRect(card.x, 400, 1, 132)
    }
    ctx.fillStyle = 'rgba(244,241,234,0.48)'
    ctx.font = '600 13px system-ui, sans-serif'
    ctx.fillText(card.label, card.x + 28, 420)
    ctx.fillStyle = i === 2 && data.growthPct !== null && data.growthPct < 0 ? '#f87171' : '#f8f4ec'
    if (i === 2 && data.growthPct !== null && data.growthPct >= 0) ctx.fillStyle = '#4ade80'
    ctx.font = '700 42px system-ui, sans-serif'
    ctx.fillText(card.value, card.x + 28, 478)
  })

  roundRect(ctx, 72, 592, 936, 560, 32)
  ctx.fillStyle = '#121014'
  ctx.fill()
  ctx.strokeStyle = 'rgba(255,255,255,0.06)'
  ctx.stroke()

  ctx.fillStyle = 'rgba(244,241,234,0.5)'
  ctx.font = '600 15px system-ui, sans-serif'
  ctx.fillText('Comparativo diário', 108, 640)
  ctx.fillStyle = '#f8f4ec'
  ctx.font = '600 18px system-ui, sans-serif'
  ctx.fillText(`Pico ${data.peakLabel} · ${formatNumber(data.peakValue)}`, 108, 670)

  const chartX = 108
  const chartY = 710
  const chartW = 864
  const chartH = 340
  const days = data.days
  const maxValue = Math.max(...days.map(d => d.value), 1)
  const gap = days.length > 20 ? 6 : days.length > 10 ? 10 : 14
  const barW = days.length > 0 ? Math.max(8, (chartW - gap * (days.length - 1)) / days.length) : 20

  ctx.strokeStyle = 'rgba(255,255,255,0.06)'
  ctx.lineWidth = 1
  for (let i = 0; i < 4; i++) {
    const y = chartY + (chartH / 4) * i
    ctx.beginPath()
    ctx.moveTo(chartX, y)
    ctx.lineTo(chartX + chartW, y)
    ctx.stroke()
  }

  const peakValue = data.peakValue
  days.forEach((day, i) => {
    const h = Math.max(6, (day.value / maxValue) * chartH)
    const x = chartX + i * (barW + gap)
    const y = chartY + chartH - h
    const isPeak = day.value === peakValue && peakValue > 0
    const barGrad = ctx.createLinearGradient(x, y, x, y + h)
    if (isPeak) {
      barGrad.addColorStop(0, '#f87171')
      barGrad.addColorStop(1, '#991b1b')
    } else {
      barGrad.addColorStop(0, '#fb7185')
      barGrad.addColorStop(1, '#7f1d1d')
    }
    ctx.fillStyle = barGrad
    roundRect(ctx, x, y, barW, h, Math.min(10, barW / 2))
    ctx.fill()
  })

  ctx.fillStyle = 'rgba(244,241,234,0.42)'
  ctx.font = '500 16px system-ui, sans-serif'
  const labelStep = days.length <= 8 ? 1 : days.length <= 16 ? 2 : Math.ceil(days.length / 7)
  days.forEach((day, i) => {
    if (i % labelStep !== 0 && i !== days.length - 1) return
    const x = chartX + i * (barW + gap) + barW / 2
    ctx.textAlign = 'center'
    ctx.fillText(day.label, x, chartY + chartH + 36)
  })
  ctx.textAlign = 'left'

  ctx.fillStyle = 'rgba(244,241,234,0.38)'
  ctx.font = '500 18px system-ui, sans-serif'
  ctx.fillText(`${data.siteName}  ·  Google Analytics + Booster`, 72, 1268)
  ctx.fillStyle = 'rgba(244,241,234,0.28)'
  ctx.font = '500 16px system-ui, sans-serif'
  ctx.fillText('Pronto para o Instagram  ·  relatório editorial', 72, 1298)
}

export function downloadCanvasPng(canvas: HTMLCanvasElement, filename: string) {
  canvas.toBlob(blob => {
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
  }, 'image/png')
}
